/**
 * The interactive setup table — the analyzer's centerpiece. Lays the seats out
 * on an oval felt: tap an empty seat to set Hero (once) or add a villain, assign
 * hole cards via the CardPicker, set the effective stack.
 *
 * The board is NOT entered here anymore — the ACTION reveals the streets, and
 * each street's cards are placed in its own action section. The felt only shows
 * a READ-ONLY, centered display of the community cards revealed so far (for
 * context); primary board ENTRY lives in the street sections.
 *
 * The felt aesthetic (radial-gradient oval, blue rim, glow, seat pills) is
 * lifted from the read-only `chat/PokerTable`, rebuilt here as an EDITABLE
 * surface. Seat geometry comes from the shared `seatLayout` helpers; hero sits
 * at the bottom. Blinds (SB 0.5 / BB 1) are always shown on the felt as posted
 * chips — even on a dead seat nobody is sitting in — so the user never forgets
 * the 1bb is out.
 *
 * Pure presentation + callbacks; the BuiltHand draft + all mutation live in
 * HandBuilder.
 */

import { useState } from "react";

import {
  POSITIONS_CLOCKWISE,
  seatPositionPct,
  type Position,
} from "@/features/chat/lib/seatLayout";
import { PlayingCard } from "@/features/chat/components/PlayingCard";
import { cardsDisabledFor } from "../lib/builtHand";
import { CardSlot } from "./CardSlot";

/** What sits at a seat, from the builder's perspective. */
export interface SeatModel {
  position: Position;
  /** "hero" | "villain" | null (empty/dead). */
  role: "hero" | "villain" | null;
  /** Hero/villain hole cards (villain may be null = unknown). */
  cards: [string, string] | null;
  /** Posted blind on the felt, if this is SB/BB (always shown). */
  blind: number | null;
  /** Highlight this seat as the one to act (per-street guidance). */
  toAct: boolean;
}

export function HandTable({
  seats,
  revealedBoard,
  effectiveStackBb,
  heroPlaced,
  canAddVillain,
  usedCards,
  onSetHero,
  onAddVillain,
  onRemoveSeat,
  onSetCard,
  onToggleVillainShown,
  onStackChange,
}: {
  /** Exactly 9 seats in canonical (clockwise) order. */
  seats: SeatModel[];
  /** Community cards revealed so far (read-only display); board ENTRY lives in
   *  the street sections. */
  revealedBoard: string[];
  effectiveStackBb: number;
  heroPlaced: boolean;
  canAddVillain: boolean;
  /** Disabled cards for a slot = used cards minus what the slot holds. */
  usedCards: Set<string>;
  onSetHero: (position: Position) => void;
  onAddVillain: (position: Position) => void;
  onRemoveSeat: (position: Position) => void;
  onSetCard: (position: Position, idx: 0 | 1, token: string | null) => void;
  onToggleVillainShown: (position: Position, shown: boolean) => void;
  onStackChange: (value: string) => void;
}) {
  // Hero anchored at bottom; rotate so the felt always reads "you at 6 o'clock"
  // when hero is placed, else show the canonical order so every seat is pickable.
  const heroPos = seats.find((s) => s.role === "hero")?.position;
  const rotated = rotateForHero(heroPos);

  const disabledFor = (current: string | null) =>
    cardsDisabledFor(usedCards, current);

  return (
    <div className="space-y-4">
      <div
        className="relative mx-auto w-full aspect-[0.82/1] sm:aspect-[1.3/1]"
        style={{ maxWidth: "560px" }}
      >
        {/* Felt */}
        <div
          className="absolute inset-0 rounded-[50%/35%]"
          style={{
            background:
              "radial-gradient(ellipse at 50% 45%, #0d1c3a 0%, #050811 80%)",
            border: "2px solid #5e7be8",
            boxShadow:
              "inset 0 0 50px rgba(124, 147, 241, 0.22), 0 0 24px rgba(124, 147, 241, 0.18), 0 28px 60px -24px rgba(0, 0, 0, 0.85)",
          }}
        />

        {/* Seats */}
        {rotated.map((position, i) => {
          const seat = seats.find((s) => s.position === position)!;
          return (
            <SeatPill
              key={position}
              seat={seat}
              index={i}
              heroPlaced={heroPlaced}
              canAddVillain={canAddVillain}
              disabledFor={disabledFor}
              onSetHero={() => onSetHero(position)}
              onAddVillain={() => onAddVillain(position)}
              onRemoveSeat={() => onRemoveSeat(position)}
              onSetCard={(idx, t) => onSetCard(position, idx, t)}
              onToggleShown={(shown) => onToggleVillainShown(position, shown)}
            />
          );
        })}

        {/* Community cards revealed so far — READ-ONLY, centered on the felt.
            Board ENTRY happens in the street action sections; this is context.
            Use ONE transform mechanism: in Tailwind v4 `-translate-*` emit the
            CSS `translate` property, which would stack with an inline
            `transform` and double the shift. */}
        {revealedBoard.length > 0 && (
          <div
            className="absolute left-1/2 top-1/2 flex items-center justify-center gap-1.5"
            style={{ transform: "translate(-50%, -50%)" }}
            aria-label="Community cards"
          >
            {revealedBoard.map((c, i) => (
              <PlayingCard key={`${c}-${i}`} token={c} size="md" />
            ))}
          </div>
        )}
      </div>

      {/* Effective stack input — below the felt. */}
      <label className="mx-auto flex w-fit items-center gap-2 text-sm text-text-muted">
        <span className="font-medium text-text">Effective stacks</span>
        <input
          type="number"
          inputMode="decimal"
          min={1}
          value={effectiveStackBb || ""}
          onChange={(e) => onStackChange(e.target.value)}
          placeholder="100"
          aria-label="Effective stack in big blinds"
          className="w-20 rounded-lg border border-border bg-bg px-2.5 py-1.5 text-center text-text focus:border-accent/50 focus:outline-none"
        />
        <span>bb</span>
      </label>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SeatPill — one editable seat. Empty seats offer "Set as Hero" / "Add villain";
// placed seats show the label, hole-card slots, blind chip, and a remove (×).
// ---------------------------------------------------------------------------

function SeatPill({
  seat,
  index,
  heroPlaced,
  canAddVillain,
  disabledFor,
  onSetHero,
  onAddVillain,
  onRemoveSeat,
  onSetCard,
  onToggleShown,
}: {
  seat: SeatModel;
  index: number;
  heroPlaced: boolean;
  canAddVillain: boolean;
  disabledFor: (current: string | null) => Set<string>;
  onSetHero: () => void;
  onAddVillain: () => void;
  onRemoveSeat: () => void;
  onSetCard: (idx: 0 | 1, token: string | null) => void;
  onToggleShown: (shown: boolean) => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const { left, top } = seatPositionPct(index);
  const placed = seat.role !== null;
  const isHero = seat.role === "hero";

  return (
    <div
      className="absolute select-none"
      style={{
        left: `${left}%`,
        top: `${top}%`,
        transform: "translate(-50%, -50%)",
        zIndex: menuOpen ? 30 : placed ? 20 : 10,
      }}
    >
      <div className="flex flex-col items-center gap-1">
        {/* Cards above the pill for hero/villain */}
        {placed && (
          <div className="flex gap-1">
            {seat.cards === null && !isHero ? (
              <button
                type="button"
                onClick={() => onToggleShown(true)}
                className="rounded-md border border-dashed border-border px-2 py-1 text-[10px] text-text-muted hover:border-accent hover:text-accent"
              >
                + show
              </button>
            ) : (
              [0, 1].map((c) => (
                <CardSlot
                  key={c}
                  token={(seat.cards?.[c] as string) || null}
                  size="sm"
                  pickerTitle={isHero ? "Hero hole card" : "Villain card"}
                  disabled={disabledFor((seat.cards?.[c] as string) || null)}
                  onPick={(t) => onSetCard(c as 0 | 1, t)}
                  onClear={() =>
                    isHero ? onSetCard(c as 0 | 1, null) : onToggleShown(false)
                  }
                  ariaLabel={`${isHero ? "Hero" : seat.position} card ${c + 1}`}
                />
              ))
            )}
          </div>
        )}

        {/* The pill itself */}
        <div className="relative">
          <button
            type="button"
            onClick={() => {
              if (placed) return;
              setMenuOpen((o) => !o);
            }}
            className={[
              "rounded-full px-2.5 py-1 text-center min-w-[52px] text-[11px] font-semibold tracking-wide transition",
              "bg-bg/95 backdrop-blur-sm",
              seat.toAct
                ? "border-2 border-accent text-text shadow-[0_0_16px_rgba(124,147,241,0.7)]"
                : isHero
                  ? "border-2 border-brand text-text"
                  : placed
                    ? "border-2 border-accent text-text"
                    : "border border-dashed border-border text-text-muted hover:border-accent hover:text-accent",
            ].join(" ")}
          >
            <div>{seat.position}</div>
            {isHero && <div className="text-[9px] font-medium text-brand">HERO</div>}
            {seat.blind !== null && (
              <div className="text-[9px] font-mono text-accent">
                ·{seat.blind}
              </div>
            )}
          </button>

          {/* Remove (×) on a placed seat */}
          {placed && (
            <button
              type="button"
              onClick={onRemoveSeat}
              aria-label={`Remove ${seat.position}`}
              className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full border border-border bg-surface text-[10px] leading-none text-text-muted shadow hover:text-danger"
            >
              ×
            </button>
          )}

          {/* Empty-seat action menu */}
          {!placed && menuOpen && (
            <div className="absolute left-1/2 top-full z-40 mt-1 flex -translate-x-1/2 flex-col gap-1 rounded-lg border border-border bg-surface p-1 shadow-lg">
              {!heroPlaced && (
                <button
                  type="button"
                  onClick={() => {
                    onSetHero();
                    setMenuOpen(false);
                  }}
                  className="whitespace-nowrap rounded-md px-2.5 py-1.5 text-left text-xs text-text hover:bg-accent/15"
                >
                  Set as Hero
                </button>
              )}
              <button
                type="button"
                disabled={!canAddVillain}
                onClick={() => {
                  onAddVillain();
                  setMenuOpen(false);
                }}
                className="whitespace-nowrap rounded-md px-2.5 py-1.5 text-left text-xs text-text hover:bg-accent/15 disabled:opacity-40"
              >
                Add villain
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rotation — hero at index 0 (bottom). When hero isn't placed yet, keep the
// canonical order so every seat is in its natural spot and pickable.
// ---------------------------------------------------------------------------

function rotateForHero(heroPos: Position | undefined): Position[] {
  if (!heroPos) return [...POSITIONS_CLOCKWISE];
  const idx = POSITIONS_CLOCKWISE.indexOf(heroPos);
  if (idx < 0) return [...POSITIONS_CLOCKWISE];
  return [
    ...POSITIONS_CLOCKWISE.slice(idx),
    ...POSITIONS_CLOCKWISE.slice(0, idx),
  ];
}
